<script setup lang="ts">
import { watch, ref, computed } from 'vue'
import { FormKit } from '@formkit/vue'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'

const props = defineProps<{
  isOpen: boolean
  initialData: any
  billingProfiles: any[]
}>()

const emit = defineEmits<{
  (e: 'openChange', value: boolean): void
  (e: 'save', value: any): void
}>()

const isOpen = computed({
  get: () => props.isOpen,
  set: (val: boolean) => emit('openChange', val),
})

const values = ref({
  ...props.initialData,
  fakturacia_zaplatene_do: props.initialData?.fakturacia_zaplatene_do ?? '',
  documents_renewal_fakturacne_udaje_id: props.initialData?.documents_renewal_fakturacne_udaje_id ?? null,
  vhq_renewal_fakturacne_udaje_id: props.initialData?.vhq_renewal_fakturacne_udaje_id ?? null,
})

function handleOpenChange(val: boolean) {
  emit('openChange', val)
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      values.value = { ...props.initialData }
    }
  },
)

function onSubmit() {
  emit('save', { ...values.value })
  emit('openChange', false)
}
</script>

<template>
  <Dialog :open="isOpen" @update:open="handleOpenChange">
    <DialogContent class="max-w-[calc(100%-2rem)] rounded-3xl p-6 sm:max-w-146 sm:p-10">
      <DialogHeader class="gap-2 pe-14 text-start">
        <DialogTitle>Fakturácia a obnovy</DialogTitle>
        <DialogDescription>Upravte fakturačné a obnovené údaje spoločnosti.</DialogDescription>
      </DialogHeader>
      <form novalidate @submit="onSubmit">
        <div class="overflow-y-auto max-h-[calc(100vh-4rem)] space-y-6">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5 sm:gap-6">
            <!-- Balík zaplatený do -->
            <div>
              <label for="fakturacia_zaplatene_do" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Balík zaplatený do
              </label>
              <FormKit
                type="text"
                id="fakturacia_zaplatene_do"
                name="fakturacia_zaplatene_do"
                v-model="values.fakturacia_zaplatene_do"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              />
            </div>

            <!-- Fakturačný profil pre auto-obnovu Dokladov -->
            <div>
              <label for="documents_renewal_fakturacne_udaje_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Fakturačný profil pre auto-obnovu Dokladov
              </label>
              <FormKit
                type="select"
                id="documents_renewal_fakturacne_udaje_id"
                name="documents_renewal_fakturacne_udaje_id"
                v-model="values.documents_renewal_fakturacne_udaje_id"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              >
                <option :value="null">Predvolené (posledná uhradená objednávka)</option>
                <option v-for="profile in billingProfiles" :key="profile.id" :value="profile.id">
                  {{ profile.name }}
                </option>
              </FormKit>
            </div>

            <!-- Fakturačný profil pre auto-obnovu sídla -->
            <div>
              <label for="vhq_renewal_fakturacne_udaje_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Fakturačný profil pre auto-obnovu sídla
              </label>
              <FormKit
                type="select"
                id="vhq_renewal_fakturacne_udaje_id"
                name="vhq_renewal_fakturacne_udaje_id"
                v-model="values.vhq_renewal_fakturacne_udaje_id"
                class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-500"
              >
                <option :value="null">Predvolené (posledná uhradená objednávka)</option>
                <option v-for="profile in billingProfiles" :key="profile.id" :value="profile.id">
                  {{ profile.name }}
                </option>
              </FormKit>
            </div>
          </div>
        </div>
        <DialogFooter class="mx-0 mb-0 gap-3 p-0 pt-2 sm:justify-end">
          <div class="flex flex-col-reverse justify-end gap-3 pt-3 sm:flex-row">
            <Button variant="outline" type="button" @click="handleOpenChange(false)">
              Zrušiť
            </Button>
            <Button type="submit">Uložiť</Button>
          </div>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>